import {dollarsToCpmMicros, mediationLineDisplayNameForSubmit} from '../mediationDraft.ts';
import type {DryRunStep, MissingApiNote} from '../mediationDraft.ts';
import {
    VUNGLE_SOURCE_ID,
    parseVunglePlacementIdFromApplyData,
    resolveVungleAdapter,
    vunglePlacementType,
} from './partnerAdapters.ts';
import type {BuildConfirmStepsContext, BuildConfirmStepsResult, WaterfallSourceAdapter} from './types.ts';

export {parseVunglePlacementIdFromApplyData};

export type BuildVungleMappingChangeInput = {
    appId: string;
    placementId: string;
    displayName?: string;
    platform?: string;
    format?: string;
};

export function buildVungleMappingChange(
    input: BuildVungleMappingChangeInput,
): Record<string, unknown> {
    const resolved = resolveVungleAdapter(input.platform, input.format);
    const change: Record<string, unknown> = {
        adapterId: resolved.adapterId,
        adSourceId: VUNGLE_SOURCE_ID,
        adUnitConfigurations: {
            [resolved.configs.appId]: String(input.appId || '').trim(),
            [resolved.configs.placementId]: String(input.placementId || '').trim(),
        },
    };
    const displayName = String(input.displayName || '').trim();
    if (displayName) {
        change.displayName = displayName;
    }
    return change;
}

export type BuildVungleCreateChangeInput = {
    displayName: string;
    format: string;
};

export function buildVungleCreateChange(
    input: BuildVungleCreateChangeInput,
): Record<string, unknown> {
    return {
        name: String(input.displayName || '').trim(),
        type: vunglePlacementType(input.format),
        allowEndCards: true,
        isHBParticipation: false,
    };
}

function partnerAppIdFrom(ctx: BuildConfirmStepsContext): string {
    const row = ctx.row as {partnerAppId?: string};
    return String(ctx.partnerAppId || row.partnerAppId || '').trim();
}

function mappingPathFor(ctx: BuildConfirmStepsContext): string {
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    return mappingResourceId
        ? `/admob/accounts/${ctx.accountId}/ad-units/${mappingResourceId}/mappings`
        : `/admob/accounts/${ctx.accountId}/ad-units/{standard_ad_unit_resource_id}/mappings`;
}

function buildAddWaterfallLineStep(
    ctx: BuildConfirmStepsContext,
    opts: {
        stepId: string;
        dependsOn: string;
        displayName: string;
        notes: string[];
        tempId: string;
        micros: string | null;
    },
): DryRunStep {
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    const lineBody: Record<string, any> = {
        id: opts.tempId,
        displayName: opts.displayName,
        adSourceId: VUNGLE_SOURCE_ID,
        cpmMode: 'MANUAL',
        state: 'ENABLED',
    };
    if (opts.micros != null) {
        lineBody.cpmMicros = opts.micros;
    }
    return {
        id: opts.stepId,
        kind: 'add-waterfall-line',
        label: `绑定瀑布流行 ${opts.displayName}`,
        method: 'PATCH',
        path: ctx.patchPath,
        change: {
            updateMask: `mediationGroupLines["${opts.tempId}"]`,
            mediationGroupLines: {[opts.tempId]: lineBody},
        },
        notes: opts.notes,
        dependsOn: opts.dependsOn,
        rowKey: ctx.row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    };
}

export function buildConfirmSteps(ctx: BuildConfirmStepsContext): BuildConfirmStepsResult {
    const steps: DryRunStep[] = [];
    const missingApis: MissingApiNote[] = [];
    const warnings: string[] = [];
    const row = ctx.row;
    const partnerAppId = partnerAppIdFrom(ctx);

    if (!ctx.standardAdUnitId) {
        missingApis.push({
            id: `no-targeting-ad-unit-${row.key}`,
            message: `新建行「${row.displayName || row.key}」缺少中介组 targeting.adUnitIds[0]，无法串联 Vungle → mapping → 瀑布流行。`,
        });
        return {steps, missingApis, warnings};
    }
    if (!partnerAppId) {
        missingApis.push({
            id: `no-vungle-app-${row.key}`,
            message: `新建行「${row.displayName || row.key}」缺少 Vungle App（行内额外配置），无法绑定。`,
        });
        return {steps, missingApis, warnings};
    }

    const bindMode = row.opmcBindMode === 'existing' ? 'existing' : 'create';
    const selectedId = String(row.selectedOpmcId || '').trim();
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    const displayName = row.displayName || 'vungle_line';
    const lineDisplayName = mediationLineDisplayNameForSubmit(
        row,
        ctx.draftRows || [],
        ctx.lineLabelHint || 'Vungle',
    );
    const micros = dollarsToCpmMicros(row.floorPrice);
    const mappingPath = mappingPathFor(ctx);

    if (bindMode === 'existing') {
        if (!selectedId) {
            missingApis.push({
                id: `no-vungle-placement-${row.key}`,
                message: `新建行「${row.displayName || row.key}」选择了已有 Placement，但未选择具体 Placement。`,
            });
            return {steps, missingApis, warnings};
        }
        const mappingStepId = `create-mapping-${row.key}`;
        const addLineStepId = `add-waterfall-line-${row.key}`;
        const tempId = ctx.allocTempLineId();
        const notes = ['选已有 Vungle Placement：跳过创建；Apply 时 create-mapping → 绑定 MANUAL 行。'];
        steps.push({
            id: mappingStepId,
            kind: 'create-mapping',
            label: `创建 mapping ${displayName}`,
            method: 'POST',
            path: mappingPath,
            change: buildVungleMappingChange({
                appId: partnerAppId,
                placementId: selectedId,
                displayName,
                platform: ctx.platform,
                format: ctx.adFormat,
            }),
            notes: [...notes],
            rowKey: row.key,
            standardAdUnitId: ctx.standardAdUnitId,
            standardAdUnitResourceId: mappingResourceId || undefined,
        });
        steps.push(
            buildAddWaterfallLineStep(ctx, {
                stepId: addLineStepId,
                dependsOn: mappingStepId,
                displayName: lineDisplayName,
                notes: [...notes, 'dependsOn create-mapping'],
                tempId,
                micros,
            }),
        );
        return {steps, missingApis, warnings};
    }

    const notes: string[] = [
        'Apply 时串联：创建 Vungle Placement → 创建 mapping → PATCH 绑定 MANUAL 瀑布流行。',
    ];
    const createStepId = `create-remote-${row.key}`;
    const mappingStepId = `create-mapping-${row.key}`;
    const addLineStepId = `add-waterfall-line-${row.key}`;
    const tempId = ctx.allocTempLineId();

    steps.push({
        id: createStepId,
        kind: 'create-remote',
        label: `创建 Vungle Placement ${displayName}`,
        method: 'POST',
        path: `/vungle/apps/${partnerAppId}/placements`,
        change: buildVungleCreateChange({
            displayName,
            format: ctx.adFormat || 'BANNER',
        }),
        notes: [...notes],
        rowKey: row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    });

    steps.push({
        id: mappingStepId,
        kind: 'create-mapping',
        label: `创建 mapping ${displayName}`,
        method: 'POST',
        path: mappingPath,
        change: {},
        notes: [...notes, 'dependsOn create-remote；预览 dry-run 不提交占位 change。'],
        dependsOn: createStepId,
        rowKey: row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    });

    steps.push(
        buildAddWaterfallLineStep(ctx, {
            stepId: addLineStepId,
            dependsOn: mappingStepId,
            displayName: lineDisplayName,
            notes: [...notes, 'dependsOn create-mapping'],
            tempId,
            micros,
        }),
    );

    return {steps, missingApis, warnings};
}

export const vungleAdapter: WaterfallSourceAdapter = {
    sourceId: VUNGLE_SOURCE_ID,
    displayNameHint: 'Vungle',
    supportsCreate: true,
    supportsPickExisting: true,
    hasAccountApi: false,
    buildConfirmSteps,
};
